import React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Input } from "../ui/Input";
import { Label } from "../ui/Label";
import { Button } from "../ui/Button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/Card";
import { Link } from "react-router-dom";
import { EnvelopeClosedIcon, ArrowRightIcon } from "@radix-ui/react-icons";

const resetRequestSchema = z.object({
  email: z.string().email("Invalid email address"),
});

type ResetRequestSchemaType = z.infer<typeof resetRequestSchema>;

interface PasswordResetRequestProps {
  onSubmit: (data: ResetRequestSchemaType) => Promise<void>;
  isLoading?: boolean;
}

export const PasswordResetRequest: React.FC<PasswordResetRequestProps> = ({
  onSubmit,
  isLoading = false,
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<ResetRequestSchemaType>({
    resolver: zodResolver(resetRequestSchema),
    mode: "onChange",
  });

  return (
    <Card className="w-full max-w-md bg-white shadow-none border border-border-gray rounded-none">
      <CardHeader className="space-y-3 pb-6 pt-8">
        <CardTitle className="text-center text-xl font-bold uppercase tracking-wide">
          PASSWORD RESET
        </CardTitle>
        <CardDescription className="text-center text-sm text-gray-600">
          Provide the E-mail Address associated with
          <br />
          your account to recover your password
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4 px-8 pb-8">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-0">
          <div className="relative pb-5">
            <Label htmlFor="email" className="sr-only">
              Email
            </Label>
            <Input
              id="email"
              type="email"
              placeholder="E-mail"
              {...register("email")}
              error={!!errors.email}
              className="w-full h-12 bg-disabled-bg border-border-gray rounded-none placeholder:text-gray-500"
              disabled={isLoading}
            />
            {errors.email && (
              <p className="text-xs text-red-500 absolute left-0 top-[calc(100%-1.25rem)]">
                {errors.email.message}
              </p>
            )}
          </div>

          <div className="space-y-3 pt-2">
            <Button
              type="submit"
              className="w-full h-12 bg-primary-blue hover:bg-primary-blue/90 text-white font-normal rounded-none"
              disabled={isLoading || !isValid}
            >
              {isLoading ? "Sending..." : "Reset Password"}
            </Button>

            <Link to="/login" className="block">
              <Button
                type="button"
                variant="secondary"
                className="w-full h-12 bg-black hover:bg-black/90 text-white font-normal rounded-none"
              >
                Back to Login
              </Button>
            </Link>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};
